package org.example.project.engineservice

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.encodeToString
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.ServiceDocumentViewerService
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.YardDocumentSnapshot
import org.example.project.service.YardDocumentSnapshotSaveRequest
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.example.project.engineservice.cr.CrPreviewLayout
import org.example.project.enginetabular.loadWithTabularFilters

object ServiceApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    private fun currentFirmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun triggerSupportRefresh() {
        GlobalManager.markJsonChanged("support", "vertragsdaten/support/support.json")
    }

    private fun triggerCrRefresh() {
        GlobalManager.markJsonChanged("cr", "vertragsdaten/cr/cr.json")
    }

    private fun triggerServiceDocumentRefresh(type: String) {
        when (type.trim().lowercase()) {
            "support" -> triggerSupportRefresh()
            "cr" -> triggerCrRefresh()
        }
    }

    suspend fun loadSupport(mode: String = "all"): List<All> =
        decodeList(LumaticaApiManager.call("supportticket/list/${apiPart(mode)}/${apiPart(LoginService.getId())}/${apiPart(currentFirmenId())}"))

    suspend fun searchSupport(mode: String = "all", filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "supportticket/list/${apiPart(mode)}/${apiPart(LoginService.getId())}/${apiPart(currentFirmenId())}",
                filters
            )
        )

    suspend fun createSupport(screen: String, problem: String): All? {
        val saved = decodeOne(
            LumaticaApiManager.call(
                "supportticket/create/${apiPart(LoginService.getId())}/${apiPart(currentFirmenId())}/${apiPart(screen)}/${apiPart(problem)}"
            )
        )
        if (saved != null) triggerSupportRefresh()
        return saved
    }

    fun supportPdfFileName(ticket: All): String {
        val firmenId = ticket.firmenId.ifBlank { "firma" }
        val monthKey = serviceMonthKey(ticket.bearbeitetAm.ifBlank { ticket.erstelltAm }.ifBlank { ticket.datum })
        val nummer = serviceSequence(ticket.ticketnummer.ifBlank { ticket.supportTicket }, ticket.id)
        return "${firmenId}_${monthKey}_${nummer}_support.pdf"
    }

    fun crPdfFileName(ticket: All): String {
        val firmenId = ticket.firmenId.ifBlank { "firma" }
        val monthKey = serviceMonthKey(ticket.bearbeitetAm.ifBlank { ticket.erstelltAm }.ifBlank { ticket.datum })
        val nummer = serviceSequence(ticket.crNummer.ifBlank { ticket.id }, ticket.id)
        return "${firmenId}_${monthKey}_${nummer}_cr.pdf"
    }

    fun supportMitarbeiterName(user: All): String =
        listOf(user.vorname, user.nachname)
            .filter { it.isNotBlank() }
            .joinToString(" ")
            .ifBlank { user.personalnummer }
            .ifBlank { user.id }

    suspend fun loadSupportPromptMarkdown(): String = runCatching {
        val res = window.fetch("supportdocs/LUMATICA_KI_SUPPORT_PROMPT.md").await()
        if (res.ok) res.text().await() else ""
    }.getOrDefault("")

    suspend fun loadSupportSystemMarkdown(): String = runCatching {
        val res = window.fetch("supportdocs/SYSTEMDOKUMENTATION_lumatica.md").await()
        if (res.ok) res.text().await() else ""
    }.getOrDefault("")

fun buildKiSupportMarkdown(
        ticket: All,
        promptMd: String,
        systemMd: String,
        kiAuswahl: String
    ): String {
        val screen = ticket.screenKey
        return """
$promptMd

## SYSTEMDOKUMENTATION LUMATICA
$systemMd

## SUPPORTFALL
Ticket: ${ticket.ticketnummer}
FirmenId: ${ticket.firmenId}
Firma: ${ticket.firma}
Standort: ${ticket.standort}
Mitarbeiter: ${supportMitarbeiterName(ticket)}
Personalnummer: ${ticket.personalnummer}
Fehlerort: $screen
Problembeschreibung:
${ticket.problembeschreibung}
KI-Auswahl: $kiAuswahl
""".trimIndent()
    }

    suspend fun resumeSupport(): All? =
        decodeOne(LumaticaApiManager.call("supportticket/resume/${apiPart(LoginService.getId())}/${apiPart(currentFirmenId())}"))

    suspend fun markSupportKi(ticketId: String, provider: String): All? {
        val saved = decodeOne(LumaticaApiManager.call("supportki/mark-ki/${apiPart(ticketId)}/${apiPart(provider)}"))
        if (saved != null) triggerSupportRefresh()
        return saved
    }

    suspend fun finishSupport(
        ticketId: String,
        solved: Boolean,
        loesungsweg: String,
        begruendung: String,
        provider: String
    ): All? {
        if (ticketId.isBlank()) return null
        val response = window.fetch(
            "${org.example.project.manager.LumaticaApiSettings.getBaseUrl()}/api/supportabschluss/${if (solved) "ki-geloest" else "ki-nicht-geloest"}/${apiPart(ticketId)}",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("ticketId", ticketId)
                        put("solved", solved)
                        put("loesungsweg", loesungsweg)
                        put("begruendung", begruendung)
                        put("provider", provider)
                        put("userId", LoginService.getId())
                    }
                )
            )
        ).await()
        val raw = response.text().await()
        if (!response.ok) {
            console.error("Support finish failed", response.status, raw)
            return null
        }
        val saved = decodeOne(raw)
        if (saved != null) triggerSupportRefresh()
        return saved
    }

    suspend fun closeSupportManual(
        ticketId: String,
        fehlerursache: String,
        loesung: String,
        kostenpflichtig: Boolean
    ): All? {
        val saved = decodeOne(
            LumaticaApiManager.call(
                "supportabschluss/mitarbeiter-schliesst/${apiPart(ticketId)}/${apiPart(LoginService.getId())}/${apiPart(fehlerursache)}/${apiPart(loesung)}/${apiPart(kostenpflichtig.toString())}"
            )
        )
        if (saved != null) triggerSupportRefresh()
        return saved
    }

    suspend fun openSupportDocument(ticketId: String, fileName: String, logMissing: Boolean = true): Boolean =
        openServiceDocument("support", ticketId, fileName, logMissing)

    suspend fun openCrDocument(crId: String, fileName: String, logMissing: Boolean = true): Boolean =
        openServiceDocument("cr", crId, fileName, logMissing)

    suspend fun serviceDocuments(type: String, entryId: String): List<All> =
        decodeList(LumaticaApiManager.call("servicedocumentscreen/list/${apiPart(type)}/${apiPart(entryId)}"))

    suspend fun openServiceDocument(type: String, entryId: String, fileName: String, logMissing: Boolean = true): Boolean {
        val cleanFileName = fileName.trim()
        if (type.lowercase().startsWith("yard-")) {
            val snapshotFileName = cleanFileName.replace(Regex("\\.pdf$", RegexOption.IGNORE_CASE), ".json")
            val loaded = loadYardDocumentSnapshot(type, entryId, snapshotFileName)
            if (loaded != null) {
                ServiceDocumentViewerService.showSnapshot(snapshotFileName, loaded)
                return true
            }
        }
        val blobUrl = fetchServiceDocumentBlobUrl(documentOpenPath(type, entryId, cleanFileName), cleanFileName, logMissing)
        if (blobUrl.isBlank()) return false
        ServiceDocumentViewerService.show(cleanFileName, blobUrl)
        return true
    }

    suspend fun openServiceDocumentMeta(type: String, entryId: String, doc: All, logMissing: Boolean = true): Boolean {
        val fileName = doc.file
            .ifBlank { doc.name.takeIf { it.isNotBlank() }?.let { "$it.pdf" }.orEmpty() }
        if (fileName.isBlank()) return false
        return openServiceDocument(type, entryId, fileName, logMissing)
    }

    private suspend fun loadYardDocumentSnapshot(type: String, entryId: String, fileName: String): YardDocumentSnapshot? {
        if (!fileName.lowercase().endsWith(".json")) return null
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/servicedocumentscreen/snapshot/${apiPart(type)}/${apiPart(entryId)}/${apiPart(fileName)}",
            RequestInit(headers = serviceDocumentHeaders())
        ).await()
        if (!response.ok) return null
        val raw = response.text().await()
        return runCatching { json.decodeFromString(YardDocumentSnapshot.serializer(), raw) }.getOrNull()
    }

    private suspend fun fetchServiceDocumentBlobUrl(url: String, fileName: String = "", logMissing: Boolean = true): String {
        if (url.isBlank()) return ""
        val response = window.fetch(
            url,
            RequestInit(headers = serviceDocumentHeaders())
        ).await()
        if (!response.ok) {
            val raw = runCatching { response.text().await() }.getOrDefault("")
            if (logMissing) console.error("Service document open failed", response.status, url, fileName, raw)
            return ""
        }
        val blob = response.blob().await()
        return window.asDynamic().URL.createObjectURL(blob) as String
    }

    private fun documentOpenPath(type: String, entryId: String, fileName: String): String =
        "${LumaticaApiSettings.getBaseUrl()}/api/servicedocumentscreen/open/${apiPart(type)}/${apiPart(entryId)}/${apiPart(fileName)}"

    private fun serviceDocumentHeaders(contentType: String? = null) =
        LumaticaApiManager.auditHeaders(contentType)

    suspend fun uploadServiceDocument(type: String, entryId: String, file: File, fileName: String = file.name): String {
        if (type.isBlank() || entryId.isBlank()) return ""
        val dataUrl = readFileAsDataUrl(file) ?: run {
            console.error("Service document upload failed: file could not be read", type, entryId, fileName)
            return ""
        }
        val response = window.fetch(
            "${org.example.project.manager.LumaticaApiSettings.getBaseUrl()}/api/servicedocumentscreen/upload/${apiPart(type)}/${apiPart(entryId)}",
            RequestInit(
                method = "POST",
                headers = serviceDocumentHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("fileName", fileName)
                        put("contentType", file.type)
                        put("dataUrl", dataUrl)
                    }
                )
            )
        ).await()
        val raw = response.text().await()
        if (!response.ok) {
            console.error("Service document upload failed", response.status, type, entryId, raw)
            return ""
        }
        val saved = decodeOne(raw) != null
        if (saved) triggerServiceDocumentRefresh(type)
        return if (saved) "available" else ""
    }

    suspend fun saveYardDocumentSnapshot(
        type: String,
        entryId: String,
        pdfFileName: String,
        snapshot: YardDocumentSnapshot
    ): Boolean {
        if (type.isBlank() || entryId.isBlank() || pdfFileName.isBlank()) return false
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/servicedocumentscreen/snapshot-save/${apiPart(type)}/${apiPart(entryId)}",
            RequestInit(
                method = "POST",
                headers = serviceDocumentHeaders("application/json"),
                body = json.encodeToString(YardDocumentSnapshotSaveRequest(pdfFileName, snapshot))
            )
        ).await()
        val raw = response.text().await()
        if (!response.ok) {
            console.error("Yard document snapshot save failed", response.status, type, entryId, pdfFileName, raw)
        }
        return response.ok
    }

    fun serviceDocumentOpenUrl(type: String, entryId: String, fileName: String): String =
        documentOpenPath(type, entryId, fileName)

    suspend fun serviceDocumentBlobUrl(type: String, entryId: String, fileName: String): String =
        fetchServiceDocumentBlobUrl(documentOpenPath(type, entryId, fileName), fileName)

    suspend fun loadCr(mode: String = "all"): List<All> =
        decodeList(LumaticaApiManager.call("crticket/list/${apiPart(mode)}/${apiPart(LoginService.getId())}"))

    suspend fun searchCr(mode: String = "all", filters: Map<String, String>): List<All> =
        decodeList(
            loadWithTabularFilters(
                "crticket/list/${apiPart(mode)}/${apiPart(LoginService.getId())}",
                filters
            )
        )

    suspend fun createCr(
        type: String,
        screenKey: String,
        screenLabel: String,
        betreff: String,
        beschreibung: String
    ): All? {
        val saved = decodeOne(
            LumaticaApiManager.call(
                "crticket/create/${apiPart(LoginService.getId())}/${apiPart(type)}/${apiPart(screenKey)}/${apiPart(screenLabel)}/${apiPart(betreff)}/${apiPart(beschreibung)}"
            )
        )
        if (saved != null) triggerCrRefresh()
        return saved
    }

    suspend fun sendCrOffer(
        crId: String,
        crAufwandStunden: Int,
        crAufwandFinal: Int,
        crKommentarIntern: String,
        crInhalt: String,
        angebotPdf: String
    ): All? =
        runCatching {
            val response = window.fetch(
                "${org.example.project.manager.LumaticaApiSettings.getBaseUrl()}/api/crangebot/offer/${apiPart(crId)}",
                RequestInit(
                    method = "POST",
                    headers = LumaticaApiManager.auditHeaders("application/json"),
                    body = json.encodeToString(
                        buildJsonObject {
                            put("crId", crId)
                            put("userId", LoginService.getId())
                            put("crAufwandStunden", crAufwandStunden)
                            put("crAufwandFinal", crAufwandFinal)
                            put("crKommentarIntern", crKommentarIntern)
                            put("crInhalt", crInhalt)
                            put("angebotPdf", angebotPdf)
                        }
                    )
                )
            ).await()
            val raw = response.text().await()
            if (!response.ok) {
                console.error("CR offer failed", response.status, raw)
                null
            } else {
                decodeOne(raw).also { if (it != null) triggerCrRefresh() }
            }
        }.getOrNull()

    suspend fun saveCrPreview(crId: String, preview: CrPreviewLayout): Boolean =
        runCatching {
            val response = window.fetch(
                "${org.example.project.manager.LumaticaApiSettings.getBaseUrl()}/api/crangebot/preview-save/${apiPart(crId)}/${apiPart(LoginService.getId())}",
                RequestInit(
                    method = "POST",
                    headers = LumaticaApiManager.auditHeaders("application/json"),
                    body = json.encodeToString(preview)
                )
            ).await()
            val saved = response.ok && response.text().await().contains("\"ok\":true")
            if (saved) triggerCrRefresh()
            saved
        }.getOrDefault(false)

    suspend fun loadCrPreview(crId: String): CrPreviewLayout? {
        val raw = runCatching {
            LumaticaApiManager.call("crangebot/preview-load/${apiPart(crId)}/${apiPart(LoginService.getId())}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return runCatching { json.decodeFromString(CrPreviewLayout.serializer(), raw) }.getOrNull()
    }

    suspend fun acceptCrOffer(crId: String): All? {
        val saved = decodeOne(
            LumaticaApiManager.call(
                "crannahme/accept/${apiPart(crId)}/${apiPart(LoginService.getId())}"
            )
        )
        if (saved != null) triggerCrRefresh()
        return saved
    }

    suspend fun transitionCrState(crId: String, nextState: Int, crAufwandFinal: Int? = null): All? {
        val saved = decodeOne(
            LumaticaApiManager.call(
                "crzustand/state/${apiPart(crId)}/${apiPart(LoginService.getId())}/${apiPart(nextState.toString())}/${apiPart((crAufwandFinal ?: 0).toString())}"
            )
        )
        if (saved != null) triggerCrRefresh()
        return saved
    }

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun serviceMonthKey(rawValue: String): String {
        val raw = rawValue.trim()
        return when {
            Regex("""\d{2}\.\d{2}\.\d{4}""").matches(raw) -> "${raw.takeLast(4)}_${raw.substring(3, 5)}"
            Regex("""\d{4}-\d{2}-\d{2}""").matches(raw) -> "${raw.substring(0, 4)}_${raw.substring(5, 7)}"
            Regex("""\d{4}_\d{2}""").matches(raw) -> raw
            else -> "0000_00"
        }
    }

    private fun serviceSequence(primaryValue: String, fallbackValue: String): String {
        val parsedPrimary = primaryValue.substringAfterLast("-", primaryValue).filter { it.isDigit() }.toIntOrNull()
        val parsedFallback = fallbackValue.filter { it.isDigit() }.toIntOrNull()
        return when {
            parsedPrimary != null && parsedPrimary > 0 -> parsedPrimary.toString()
            parsedFallback != null && parsedFallback > 0 -> parsedFallback.toString()
            else -> "1"
        }
    }

    private suspend fun readFileAsDataUrl(file: File): String? =
        suspendCancellableCoroutine { continuation ->
            val reader = FileReader()
            reader.onloadend = {
                continuation.resume(reader.result as? String)
                null
            }
            reader.onerror = {
                continuation.resume(null)
                null
            }
            reader.readAsDataURL(file)
        }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
